import { useCallback, useEffect, useRef, useState } from 'react';

interface UseVisibilityRefreshOptions {
  refreshInterval: number;
  isVisible: boolean;
  enabled?: boolean;
}

export const useVisibilityRefresh = (
  refreshFunction: () => void,
  options: UseVisibilityRefreshOptions
) => {
  const { refreshInterval, isVisible, enabled = true } = options;
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshRef = useRef<Date>(new Date());

  const executeRefresh = useCallback(() => {
    refreshFunction();
    const now = new Date();
    setLastRefresh(now);
    lastRefreshRef.current = now;
  }, [refreshFunction]);

  useEffect(() => {
    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Only set up interval if component is visible and enabled
    if (isVisible && enabled) {
      intervalRef.current = setInterval(() => {
        executeRefresh();
      }, refreshInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isVisible, enabled, refreshInterval, executeRefresh]);

  // Manual refresh function
  const manualRefresh = () => {
    executeRefresh();
  };

  return {
    lastRefresh,
    manualRefresh,
  };
};
